import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { User } from 'shared/models';
import { callApi } from 'apiClient';
import { updateUserSuccess } from 'actions/userActions';
import { PageHeader } from 'ui/shared/pageHeader';
import { MyProfileForm } from '../myProfileForm/MyProfileForm';
import { useSession } from 'hooks';
import { toError } from 'shared/util/toError';
import { loginSuccess } from 'actions/sessionActions';

require('./MyProfilePage.css');

export const MyProfilePage = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { user } = useSession();

    const timerRef = useRef<NodeJS.Timeout>();

    const [ busy, setBusy ] = useState(false);
    const [ successMessage, setSuccessMessage ] = useState('');
    const [ errorMessage, setErrorMessage ] = useState('');
    
    const handleChangeUser = useCallback((user: User) => {
        setBusy(true);
        void (async () => {
            try {
                const updatedUser = await callApi('PUT', `/api/users/${user.id}`, user);
                dispatch(updateUserSuccess(updatedUser));
                setSuccessMessage(t('SAVED_MESSAGE'));
                timerRef.current = setTimeout(() => setSuccessMessage(''), 4000);
            }
            catch (e) {
                const err = toError(e);
                setErrorMessage(t([ `USERS.ERROR.${err.message}`, `ERROR.${err.message}`, 'SERVER_ERROR' ]));
                timerRef.current = setTimeout(() => setErrorMessage(''), 4000);
            }
            finally {
                setBusy(false);
            }
        })();
    }, [ timerRef, dispatch, setBusy, setErrorMessage, t ]);

    const handleUnassume = useCallback(() => {
        setBusy(true);
        void (async () => {
            try {
                if (!user?.realUserId) return;
                const sessionData = await callApi('PUT', `/api/users/unassume`);
                setSuccessMessage('');
                dispatch(loginSuccess(sessionData));
            }
            catch (e) {
                const err = toError(e);
                setErrorMessage(t([ `USERS.ERROR.${err.message}`, `ERROR.${err.message}`, 'SERVER_ERROR' ]));
                timerRef.current = setTimeout(() => setErrorMessage(''), 4000);
            }
            finally {
                setBusy(false);
            }
        })();
    }, [ user, timerRef, dispatch, setBusy, setErrorMessage, t ]);

    // Clean up the timer in case it is active when we unmount
    useEffect(() => {
        return () => {
            // eslint-disable-next-line
            clearTimeout(timerRef.current!);
        };
    }, [ timerRef ]);

    if (!user) {
        return null;
    }
    return (
        <div id="my-profile-page">
            <PageHeader
                title={ t('PROFILE.TITLE') }
            />
            <div id="page-content">
                <div className="page">
                    <MyProfileForm
                        user={ user }
                        busy={ busy }
                        onChange={ handleChangeUser }
                        onUnassume={ handleUnassume }
                        errorMessage={ errorMessage }
                        successMessage={ successMessage }
                    />
                </div>
            </div>
        </div>
    );
};
